import { parseEquation } from './chemicalEquation';
import {
  MASS_CASES,
  evaluateMassModel,
  molarMass,
  type MassCase,
  type MassModelResult,
  type SystemType,
} from './conservationOfMass';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError } from './modelInputContract';

/**
 * Explore calculation for the Conservation of Mass Lab (chemistry/CLAUDE.md "Interactive
 * calculation standard"). The learner sets the mass of the limiting reactant, open or closed and how
 * far the reaction has gone; the worked steps, balance reading and 3D flask all come from here.
 * Reactions reuse the reviewed MASS_CASES (equations, apparatus masses, gases) through `massModel`.
 */

export interface ConservationSetup {
  limitingMassG: number;
  progressPercent: number;
}

export type ConservationInput = keyof ConservationSetup;

export interface ExploreReaction {
  id: string;
  label: string;
  limitingName: string;
  openCaseId: string;
  /** Case used for the closed system; null when the model does not cover a closed version. */
  closedCaseId: string | null;
  closedReason?: string;
  /** Classroom range for the limiting reactant; the other reactant stays in excess across it (pending teaching review). */
  massRange: { min: number; max: number; step: number };
  /** Apparatus in the 3D view; `none` shows the 2D balance instead (heated on a mat or in a crucible). */
  scene: 'flask' | 'none';
  solid: 'chips' | 'tablet' | 'none';
}

const OPEN_ONLY_REASON =
  'In a sealed container the metal could only use the oxygen in the trapped air, so this model covers the open bench only.';

export const EXPLORE_REACTIONS: ExploreReaction[] = [
  {
    id: 'marble-acid',
    label: 'Marble chips in hydrochloric acid',
    limitingName: 'calcium carbonate',
    openCaseId: 'carbonate-acid-open',
    closedCaseId: 'carbonate-acid-sealed',
    massRange: { min: 0.05, max: 2.5, step: 0.01 },
    scene: 'flask',
    solid: 'chips',
  },
  {
    id: 'fizzy-tablet',
    label: 'Fizzy tablet in water',
    limitingName: 'sodium hydrogencarbonate',
    openCaseId: 'tablet-open',
    closedCaseId: 'tablet-sealed',
    massRange: { min: 0.1, max: 3, step: 0.01 },
    scene: 'flask',
    solid: 'tablet',
  },
  {
    id: 'precipitate',
    label: 'Copper(II) sulfate and sodium hydroxide',
    limitingName: 'copper(II) sulfate',
    openCaseId: 'precipitation',
    closedCaseId: 'precipitation',
    massRange: { min: 0.1, max: 3, step: 0.01 },
    scene: 'flask',
    solid: 'none',
  },
  {
    id: 'steel-wool',
    label: 'Steel wool burns in air',
    limitingName: 'iron',
    openCaseId: 'steel-wool',
    closedCaseId: null,
    closedReason: OPEN_ONLY_REASON,
    massRange: { min: 0.1, max: 5, step: 0.01 },
    scene: 'none',
    solid: 'none',
  },
  {
    id: 'magnesium',
    label: 'Magnesium ribbon in a crucible',
    limitingName: 'magnesium',
    openCaseId: 'magnesium-crucible',
    closedCaseId: null,
    closedReason: OPEN_ONLY_REASON,
    massRange: { min: 0.05, max: 0.5, step: 0.01 },
    scene: 'none',
    solid: 'none',
  },
];

export const DEFAULT_EXPLORE_REACTION_ID = EXPLORE_REACTIONS[0].id;

/**
 * Molar volume of an ideal gas at SLC (25 °C, 100 kPa), the module's stated conditions:
 * V = RT ÷ p = 8.314 × 298.15 ÷ 100 = 24.79 L/mol. Review status: pending-teaching-review.
 */
export const MOLAR_GAS_VOLUME_L_PER_MOL = 24.79;
/** The balloon on the closed flask is a model limit, not a chemistry value. */
export const BALLOON_MAX_L = 0.5;

export function findExploreReaction(id: string): ExploreReaction | undefined {
  return EXPLORE_REACTIONS.find((reaction) => reaction.id === id);
}

export function conservationFields(reaction: ExploreReaction): ModelField<ConservationInput>[] {
  return [
    { key: 'limitingMassG', label: `Mass of ${reaction.limitingName}`, unit: 'g', ...reaction.massRange },
    { key: 'progressPercent', label: 'Reaction progress', unit: '%', min: 0, max: 100, step: 1 },
  ];
}

export function defaultConservationSetup(reaction: ExploreReaction): ConservationSetup {
  const base = MASS_CASES.find((entry) => entry.id === reaction.openCaseId);
  return { limitingMassG: base?.limitingMassG ?? reaction.massRange.min, progressPercent: 100 };
}

const SUBSCRIPTS = '₀₁₂₃₄₅₆₇₈₉';
/** CO2 → CO₂ for a single formula (every digit in a formula without a coefficient is a subscript). */
export function subscriptFormula(formula: string): string {
  return formula.replace(/\d/g, (digit) => SUBSCRIPTS[Number(digit)]);
}

const grams = (value: number) => `${value.toFixed(2)} g`;
const sig3 = (value: number) => value.toLocaleString('en-AU', { minimumSignificantDigits: 3, maximumSignificantDigits: 3 });

export interface ConservationSceneState {
  /** Open neck, a rubber stopper (closed, no gas) or a balloon over the neck (closed, gas forms). */
  closure: 'open' | 'stopper' | 'balloon';
  solid: ExploreReaction['solid'];
  /** Unreacted solid as a share (0–1) of the largest mass in the range. */
  solidLeft: number;
  /** Precipitate formed as a share (0–1) of the largest amount in the range. */
  precipitate: number;
  /** Blue copper(II) ion colour still in solution (0–1). */
  blueSolution: number;
  /** Balloon filling as a share of its capacity (closed system only). */
  balloon: number;
  /** Bubble rate (0–1); zero before the reaction starts and after it ends. */
  bubbling: number;
}

export interface ConservationExploreResult {
  reaction: ExploreReaction;
  entry: MassCase;
  model: MassModelResult;
  limitingMol: number;
  steps: WorkedStep[];
  /** Balance reading at the shown progress, in g. */
  readingG: number;
  /** Progress actually shown (0–1); lower than the input when the balloon limit stops the model. */
  shownProgress: number;
  /** Gas made at the shown progress, in L at 25 °C and 100 kPa (closed flask gas only). */
  gasVolumeL: number;
  result: string;
  limitNote: string | null;
  scene: ConservationSceneState;
}

function coefficients(entry: MassCase): Map<string, number> {
  const { equation } = parseEquation(entry.equation);
  const map = new Map<string, number>();
  if (!equation) return map;
  [...equation.left, ...equation.right].forEach((compound) => map.set(compound.formula, compound.coefficient));
  return map;
}

/** The worked conservation calculation. Throws a specific message when the setup is outside the model. */
export function calculateConservationExplore(reactionId: string, system: SystemType, setup: ConservationSetup): ConservationExploreResult {
  const reaction = findExploreReaction(reactionId);
  if (!reaction) throw new Error('Choose a reaction from the reviewed list.');
  const caseId = system === 'closed' ? reaction.closedCaseId : reaction.openCaseId;
  if (!caseId) throw new Error(reaction.closedReason ?? 'This reaction is modelled as an open system only.');
  const fieldError = firstModelError(conservationFields(reaction), setup);
  if (fieldError) throw new Error(fieldError);
  const base = MASS_CASES.find((candidate) => candidate.id === caseId);
  if (!base) throw new Error('The reviewed case for this reaction is missing, so no reading is shown.');
  const entry: MassCase = { ...base, system, limitingMassG: setup.limitingMassG };
  const outcome = evaluateMassModel(entry);
  if ('reason' in outcome) throw new Error(outcome.reason);
  const { model } = outcome;
  const limitingMolar = molarMass(entry.limitingFormula);
  const coefs = coefficients(entry);
  const limitingCoef = coefs.get(entry.limitingFormula);
  if (limitingMolar === null || !limitingCoef) throw new Error(`${entry.limitingFormula} cannot be used as the limiting reactant.`);

  const limiting = subscriptFormula(entry.limitingFormula);
  const limitingMol = setup.limitingMassG / limitingMolar;
  const steps: WorkedStep[] = [
    {
      id: 'moles',
      title: `Moles of ${limiting}`,
      formula: 'n = m ÷ M',
      working: `n(${limiting}) = ${grams(setup.limitingMassG)} ÷ ${limitingMolar.toFixed(2)} g/mol = ${sig3(limitingMol)} mol`,
      status: 'done',
    },
    {
      id: 'ratio',
      title: 'Mole ratio from the balanced equation',
      formula: 'coefficients',
      working: `${model.species.map((item) => subscriptFormula(item.formula)).join(' : ')} = ${model.species.map((item) => coefs.get(item.formula)).join(' : ')}`,
      status: 'done',
    },
  ];
  model.species
    .filter((item) => item.formula !== entry.limitingFormula)
    .forEach((item) => {
      const coef = coefs.get(item.formula) ?? 0;
      const molar = molarMass(item.formula) ?? 0;
      const mol = (limitingMol * coef) / limitingCoef;
      const formula = subscriptFormula(item.formula);
      steps.push({
        id: `mass-${item.formula}`,
        title: `${item.side === 'reactant' ? 'Reacting' : 'Forming'} ${formula}`,
        formula: 'n = n(limiting) × ratio, m = n × M',
        working: `n(${formula}) = ${sig3(limitingMol)} × ${coef} ÷ ${limitingCoef} = ${sig3(mol)} mol; m = ${sig3(mol)} mol × ${molar.toFixed(2)} g/mol = ${grams(item.massG)}`,
        status: 'done',
        ...(item.gas ? { note: item.gas.direction === 'in' ? 'gas from the air' : 'gas' } : {}),
      });
    });
  const sideSum = (side: 'reactant' | 'product') =>
    model.species.filter((item) => item.side === side).map((item) => item.massG.toFixed(2)).join(' + ');
  steps.push({
    id: 'totals',
    title: 'Atoms are conserved',
    formula: 'mass of reactants = mass of products',
    working: `${sideSum('reactant')} = ${grams(model.reactantTotalG)} → ${sideSum('product')} = ${grams(model.productTotalG)}`,
    status: 'done',
  });

  // What the balance shows after the whole reaction.
  const crossing = model.crossings;
  let balanceWorking: string;
  if (system === 'closed') {
    balanceWorking = `Closed: nothing crosses the boundary, so the reading stays ${grams(model.beforeG)} = ${grams(model.afterG)}`;
  } else if (crossing.length === 0) {
    balanceWorking = `Open, but no gas crosses the boundary, so the reading stays ${grams(model.beforeG)}`;
  } else {
    const parts = crossing.map((item) => `${subscriptFormula(item.gas.formula)} ${item.gas.direction === 'in' ? 'joining from the air' : 'escaping'} = ${grams(item.massG)}`);
    const signed = crossing.map((item) => `${item.gas.direction === 'in' ? '+' : '−'} ${grams(item.massG)}`).join(' ');
    balanceWorking = `${parts.join('; ')}, so the balance ${model.changeG >= 0 ? 'rises' : 'drops'}: ${grams(model.beforeG)} ${signed} = ${grams(model.afterG)}`;
  }
  steps.push({ id: 'balance', title: 'Balance reading after the reaction', formula: 'after = before ± gas crossing the boundary', working: balanceWorking, status: 'done' });

  // Gas volume and the balloon limit (closed flask only).
  const gasOut = model.species.filter((item) => item.side === 'product' && item.gas);
  const fullGasMol = gasOut.reduce((total, item) => total + item.massG / (molarMass(item.formula) ?? Number.POSITIVE_INFINITY), 0);
  const fullGasL = fullGasMol * MOLAR_GAS_VOLUME_L_PER_MOL;
  const requested = setup.progressPercent / 100;
  const balloonLimited = system === 'closed' && reaction.scene === 'flask' && fullGasL * requested > BALLOON_MAX_L;
  const shownProgress = balloonLimited ? BALLOON_MAX_L / fullGasL : requested;
  if (system === 'closed' && gasOut.length > 0) {
    steps.push({
      id: 'gas',
      title: 'Gas trapped in the balloon',
      formula: 'V = n × 24.79 L/mol (25 °C, 100 kPa)',
      working: `V = ${sig3(fullGasMol)} mol × ${MOLAR_GAS_VOLUME_L_PER_MOL} L/mol = ${sig3(fullGasL)} L when all of it has reacted`,
      status: balloonLimited ? 'current' : 'done',
      note: `balloon holds ${BALLOON_MAX_L.toFixed(2)} L`,
    });
  }

  const readingG = model.beforeG + model.changeG * shownProgress;
  const percent = Math.round(shownProgress * 1000) / 10;
  steps.push({
    id: 'progress',
    title: 'Reading at this point in the reaction',
    formula: 'reading = before ± fraction reacted × change',
    working: `${grams(model.beforeG)} ${model.changeG >= 0 ? '+' : '−'} ${(shownProgress).toFixed(2)} × ${grams(Math.abs(model.changeG))} = ${grams(readingG)}`,
    status: shownProgress <= 0 ? 'not-reached' : shownProgress >= 1 ? 'done' : 'current',
    note: `${percent} % reacted`,
  });

  const maxMass = reaction.massRange.max;
  const massShare = setup.limitingMassG / maxMass;
  const gasMade = gasOut.length > 0;
  return {
    reaction,
    entry,
    model,
    limitingMol,
    steps,
    readingG,
    shownProgress,
    gasVolumeL: system === 'closed' ? fullGasL * shownProgress : 0,
    result: `Result: the balance reads ${grams(readingG)} (${grams(model.beforeG)} before, ${grams(model.afterG)} when all has reacted)`,
    limitNote: balloonLimited
      ? `${sig3(fullGasL * requested)} L of gas is more than the ${BALLOON_MAX_L.toFixed(2)} L balloon in this model holds. A real balloon could burst or pop off, so the model stops when the balloon is full (${percent} % reacted). Try a smaller mass.`
      : null,
    scene: {
      closure: system === 'open' ? 'open' : gasMade ? 'balloon' : 'stopper',
      solid: reaction.solid,
      solidLeft: reaction.solid === 'none' ? 0 : massShare * (1 - shownProgress),
      precipitate: reaction.id === 'precipitate' ? massShare * shownProgress : 0,
      blueSolution: reaction.id === 'precipitate' ? 1 - shownProgress : 0,
      balloon: system === 'closed' && gasMade ? (fullGasL * shownProgress) / BALLOON_MAX_L : 0,
      bubbling: gasMade && shownProgress > 0 && shownProgress < 1 ? 0.3 + 0.7 * massShare : 0,
    },
  };
}

export const MASS_QUESTION = 'When a reaction makes a gas, is any mass really lost?';

/** The reaction has finished at the shown progress (the balloon did not stop it). */
const finished = (result: ConservationExploreResult) => result.shownProgress >= 1;
const makesGas = (result: ConservationExploreResult) => result.model.species.some((item) => item.side === 'product' && Boolean(item.gas));

/** Teaching tasks (CT-INTERACT C7). Each check reads only the Explore result, never the UI. */
export const MASS_CHALLENGES: ExploreChallenge<ConservationExploreResult>[] = [
  {
    id: 'open-drop',
    prompt: 'Leave the flask open, let the reaction finish, and make the balance drop by 0.40–0.50 g.',
    hint: 'Less marble makes less carbon dioxide. Try about 1 g.',
    isMet: (result) => {
      const drop = result.model.beforeG - result.readingG;
      return result.entry.system === 'open' && finished(result) && drop >= 0.4 && drop <= 0.5;
    },
    why: 'The drop is the mass of gas that escaped. For marble chips, 1.00 g of CaCO₃ makes 0.44 g of CO₂ (1.00 × 44.01 ÷ 100.09). The atoms left the flask; they were not destroyed.',
  },
  {
    id: 'closed-constant',
    prompt: 'Seal the flask, let a gas-making reaction finish, and compare the reading with the start.',
    hint: 'Choose Closed. The balloon catches the gas.',
    isMet: (result) =>
      result.entry.system === 'closed' && finished(result) && makesGas(result) && Math.abs(result.readingG - result.model.beforeG) < 0.005,
    why: 'In a sealed flask the gas stays inside the system, so the reading at the end equals the reading at the start. Atoms are only rearranged, so mass is conserved.',
  },
  {
    id: 'balloon-limit',
    prompt: 'Keep it sealed and use so much reactant that the balloon fills before the reaction finishes.',
    hint: `The balloon holds ${BALLOON_MAX_L.toFixed(2)} L. How much marble makes more CO₂ than that?`,
    isMet: (result) => result.entry.system === 'closed' && !finished(result) && result.scene.balloon >= 0.999,
    why: '2.50 g of CaCO₃ would make 0.0250 mol of CO₂ = 0.62 L at 25 °C, more than the 0.50 L balloon. Mass is still conserved, but a real sealed flask builds pressure and can burst, so closed-system experiments use small amounts.',
  },
];
